import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

/**
 * Mounted invisibly inside the employee dashboard. Handles the one-time
 * consent/notice gate, then opens a lightweight signaling connection (no
 * camera/mic yet) and only turns on the mic (Supervise) or camera (Watch)
 * on-demand, the moment an admin actually requests it — never continuously.
 */
export default function SupervisionBroadcaster({ employee }) {
  const [needsConsent, setNeedsConsent] = useState(!!employee?.needs_supervision_consent);
  const [needsNotice, setNeedsNotice] = useState(!!employee?.needs_supervision_notice);
  const [declined, setDeclined] = useState(false);
  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const activeStreamRef = useRef(null);
  const stoppedRef = useRef(false);
  const reconnectTimerRef = useRef(null);

  const stopActiveSession = () => {
    activeStreamRef.current?.getTracks().forEach(t => t.stop());
    activeStreamRef.current = null;
    try { pcRef.current?.close(); } catch { /* noop */ }
    pcRef.current = null;
  };

  const handleMediaRequest = async (kind) => {
    const ws = wsRef.current;
    stopActiveSession();
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia(kind === "video" ? { video: true } : { audio: true });
    } catch {
      return; // camera/mic unavailable or denied — that request simply goes unanswered
    }
    activeStreamRef.current = stream;
    const pc = new RTCPeerConnection({ iceServers: [{ urls: "stun:stun.l.google.com:19302" }] });
    pcRef.current = pc;
    stream.getTracks().forEach(t => pc.addTrack(t, stream));
    pc.onicecandidate = (e) => {
      if (e.candidate && ws?.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "ice-candidate", candidate: e.candidate }));
      }
    };
    pc.onconnectionstatechange = () => {
      if (["failed", "closed", "disconnected"].includes(pc.connectionState) && pcRef.current === pc) {
        stopActiveSession();
      }
    };
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "offer", sdp: offer.sdp }));
  };

  const connectSignaling = () => {
    if (stoppedRef.current || !employee?.id) return;
    const wsUrl = `${process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws")}/api/ws/supervision/${employee.id}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    ws.onmessage = async (evt) => {
      const msg = JSON.parse(evt.data);
      if (msg.type === "watch-request") {
        await handleMediaRequest("video");
      } else if (msg.type === "listen-request") {
        await handleMediaRequest("audio");
      } else if (msg.type === "stop-media") {
        stopActiveSession();
      } else if (msg.type === "answer" && pcRef.current) {
        await pcRef.current.setRemoteDescription({ type: "answer", sdp: msg.sdp });
      } else if (msg.type === "ice-candidate" && msg.candidate && pcRef.current) {
        try { await pcRef.current.addIceCandidate(msg.candidate); } catch { /* ignore */ }
      }
    };
    ws.onclose = () => {
      stopActiveSession();
      if (!stoppedRef.current) reconnectTimerRef.current = setTimeout(connectSignaling, 3000);
    };
    ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
  };

  useEffect(() => {
    stoppedRef.current = false;
    if (!needsConsent && !needsNotice && !declined) connectSignaling();
    return () => {
      stoppedRef.current = true;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      stopActiveSession();
      try { wsRef.current?.close(); } catch { /* noop */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsConsent, needsNotice, declined]);

  // Fires the browser's native camera/mic permission prompt exactly once, at
  // the moment the employee has just agreed / been told about supervision —
  // then immediately releases the tracks. Browsers remember this per-device
  // grant, so every later on-demand Supervise/Watch request from admin can
  // proceed silently, with zero further prompts and zero continuous capture.
  const primePermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      stream.getTracks().forEach(t => t.stop());
    } catch { /* denied or no device — later on-demand requests simply won't succeed either */ }
  };

  const agree = async () => {
    try { await api.post("/employee/supervision/consent", { agree: true }); } catch { /* proceed anyway */ }
    await primePermission();
    setNeedsConsent(false);
  };
  const decline = async () => {
    try { await api.post("/employee/supervision/consent", { agree: false }); } catch { /* noop */ }
    setNeedsConsent(false);
    setDeclined(true);
  };
  const dismissNotice = async () => {
    try { await api.post("/employee/supervision/notice-seen"); } catch { /* noop */ }
    await primePermission();
    setNeedsNotice(false);
  };

  if (declined) {
    return (
      <div data-testid="supervision-declined-screen" className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "var(--bg-main)" }}>
        <div className="max-w-md text-center">
          <h2 className="font-serif-display text-2xl mb-2">Consent required</h2>
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
            This role requires camera/mic supervision consent to continue. Please contact your admin if you have questions.
          </p>
        </div>
      </div>
    );
  }
  if (needsConsent) {
    return (
      <div data-testid="supervision-consent-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }}>
        <div className="rounded-2xl p-6 w-full max-w-md" style={{ background: "var(--bg-card)" }}>
          <h2 className="font-serif-display text-xl mb-3">Before you continue</h2>
          <p className="text-sm mb-5" style={{ color: "var(--text-secondary)" }}>
            This role may be supervised via camera and microphone while you're logged in, for security and quality purposes. Do you agree to continue?
          </p>
          <div className="flex justify-end gap-3">
            <button data-testid="supervision-consent-decline" onClick={decline} className="btn-outline text-sm">Decline</button>
            <button data-testid="supervision-consent-agree" onClick={agree} className="btn-primary text-sm">I agree</button>
          </div>
        </div>
      </div>
    );
  }
  if (needsNotice) {
    return (
      <div data-testid="supervision-notice-banner" className="rounded-xl p-3 mb-6 text-xs flex items-center justify-between gap-3 flex-wrap"
        style={{ background: "rgba(91,117,83,0.08)" }}>
        <span>Heads up: this role may be supervised via camera and microphone during your login sessions.</span>
        <button data-testid="supervision-notice-dismiss" onClick={dismissNotice} className="underline flex-shrink-0" style={{ color: "var(--brand)" }}>Got it</button>
      </div>
    );
  }
  return null;
}
